import apiClient from './client';
import type {
  TimesheetSubmission,
  TimesheetConfig,
  PaginatedResponse,
} from '../types/timesheet-approval.types';

// -- Payload Types ----------------------------------------------------------

export interface SubmitTimesheetPayload {
  userId?: string;
  periodStart: string;
  periodEnd: string;
  note?: string;
}

export interface ApproveTimesheetPayload {
  note?: string;
}

export interface RejectTimesheetPayload {
  reason: string;
}

export interface ResubmitTimesheetPayload {
  note?: string;
}

export interface ReopenTimesheetPayload {
  reason: string;
}

export interface ListTimesheetsParams {
  status?: string;
  userId?: string;
  approverUserId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

// -- Timesheet Submission API -----------------------------------------------

export async function listTimesheets(
  params: ListTimesheetsParams,
): Promise<PaginatedResponse<TimesheetSubmission>> {
  const { data } = await apiClient.get<PaginatedResponse<TimesheetSubmission>>(
    '/timesheet-submissions',
    { params },
  );
  return data;
}

export async function getTimesheetDetail(
  id: string,
): Promise<TimesheetSubmission> {
  const { data } = await apiClient.get<TimesheetSubmission>(
    `/timesheet-submissions/${id}`,
  );
  return data;
}

export async function getPeriodStatus(
  userId: string,
  periodStart: string,
  periodEnd: string,
): Promise<TimesheetSubmission | null> {
  try {
    const { data } = await apiClient.get<TimesheetSubmission | null>(
      '/timesheet-submissions/period/status',
      { params: { userId, periodStart, periodEnd } },
    );
    return data;
  } catch {
    return null;
  }
}

export async function submitTimesheet(
  payload: SubmitTimesheetPayload,
): Promise<TimesheetSubmission> {
  const { data } = await apiClient.post<TimesheetSubmission>(
    '/timesheet-submissions',
    payload,
  );
  return data;
}

export async function approveTimesheet(
  id: string,
  payload?: ApproveTimesheetPayload,
): Promise<TimesheetSubmission> {
  const { data } = await apiClient.put<TimesheetSubmission>(
    `/timesheet-submissions/${id}/approve`,
    payload ?? {},
  );
  return data;
}

export async function rejectTimesheet(
  id: string,
  payload: RejectTimesheetPayload,
): Promise<TimesheetSubmission> {
  const { data } = await apiClient.put<TimesheetSubmission>(
    `/timesheet-submissions/${id}/reject`,
    payload,
  );
  return data;
}

export async function resubmitTimesheet(
  id: string,
  payload: ResubmitTimesheetPayload,
): Promise<TimesheetSubmission> {
  const { data } = await apiClient.put<TimesheetSubmission>(
    `/timesheet-submissions/${id}/resubmit`,
    payload,
  );
  return data;
}

export async function reopenTimesheet(
  id: string,
  payload: ReopenTimesheetPayload,
): Promise<TimesheetSubmission> {
  const { data } = await apiClient.put<TimesheetSubmission>(
    `/timesheet-submissions/${id}/reopen`,
    payload,
  );
  return data;
}

// -- Config API -------------------------------------------------------------

export async function getTimesheetConfig(): Promise<TimesheetConfig> {
  const { data } = await apiClient.get<TimesheetConfig>('/timesheet-config');
  return data;
}

export async function updateTimesheetConfig(
  payload: Partial<TimesheetConfig>,
): Promise<TimesheetConfig> {
  const { data } = await apiClient.patch<TimesheetConfig>(
    '/timesheet-config',
    payload,
  );
  return data;
}
